import moment from "moment";

/*
 * Which periods the job will NOT bill automatically, so the operator can be
 * warned while setting the fee (SMT-1366). Mirrors the rules in
 * kontax-backend recurringFee/utilities/schedule.ts:
 *
 * - a period whose Stichtag lies before Gilt ab is never billed. A fee from
 *   the 10th with Stichtag 5 therefore starts a month later; for a quarterly
 *   fee anchored on the start month it is the whole first quarter.
 * - a NEW fee never backfills: periods whose Stichtag already passed on the
 *   day it is saved are not generated.
 *
 * Both are deliberate (AC 8): such periods are recorded by hand under
 * "+ Leistung erfassen". The warning only makes the consequence visible.
 */

const DATE_FORMAT = "YYYY-MM-DD";

export interface ScheduleInput {
  rhythmInMonths: number;
  billingDay: number;
  anchorMonth: number;
  validFrom: string;
}

export interface SkippedPeriods {
  // Period starting before its Stichtag reached Gilt ab, if any.
  beforeValidFrom: string | null;
  // Scheduled periods whose Stichtag passed before today (new fees only).
  beforeToday: string[];
}

const isScheduled = (month: moment.Moment, input: ScheduleInput) => {
  if (input.rhythmInMonths <= 1) {
    return true;
  }
  const offset = month.month() - (input.anchorMonth - 1);
  return (
    ((offset % input.rhythmInMonths) + input.rhythmInMonths) %
      input.rhythmInMonths ===
    0
  );
};

const stichtagOf = (month: moment.Moment, billingDay: number) =>
  month.clone().date(billingDay).format(DATE_FORMAT);

export const getSkippedPeriods = (
  input: ScheduleInput,
  today: string,
  isNewFee: boolean
): SkippedPeriods => {
  const validFromMonth = moment(input.validFrom, DATE_FORMAT).startOf("month");
  const lastMonth = moment(today, DATE_FORMAT).startOf("month");

  /*
   * The period that covers the month of Gilt ab may have started earlier: a
   * quarter anchored on February covers September from August on. Walk back
   * to its start before checking its Stichtag.
   */
  const coveringPeriod = validFromMonth.clone();
  while (!isScheduled(coveringPeriod, input)) {
    coveringPeriod.subtract(1, "month");
  }

  let beforeValidFrom: string | null = null;
  if (stichtagOf(coveringPeriod, input.billingDay) < input.validFrom) {
    beforeValidFrom = coveringPeriod.format(DATE_FORMAT);
  }

  const beforeToday: string[] = [];
  if (isNewFee) {
    for (
      const month = validFromMonth.clone();
      month.isSameOrBefore(lastMonth);
      month.add(1, "month")
    ) {
      const stichtag = stichtagOf(month, input.billingDay);
      if (
        isScheduled(month, input) &&
        stichtag >= input.validFrom &&
        stichtag < today
      ) {
        beforeToday.push(month.format(DATE_FORMAT));
      }
    }
  }

  return { beforeValidFrom, beforeToday };
};

// "09/2026", "09 - 11/2026", "11/2026 - 01/2027", as on the invoice.
export const formatPeriod = (periodStart: string, rhythmInMonths: number) => {
  const start = moment(periodStart, DATE_FORMAT);
  if (rhythmInMonths <= 1) {
    return start.format("MM/YYYY");
  }
  const end = start.clone().add(rhythmInMonths - 1, "months");
  return start.year() === end.year()
    ? `${start.format("MM")} - ${end.format("MM/YYYY")}`
    : `${start.format("MM/YYYY")} - ${end.format("MM/YYYY")}`;
};
